import React, { useEffect, useState, useCallback } from "react";
import { getApiBase } from "../apiBase";
import {
  FaTimesCircle,
  FaFileInvoice,
  FaCreditCard,
  FaDownload,
} from "react-icons/fa";
import { FiCheckCircle, FiAlertCircle, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  FiGrid,
  FiTrendingUp,
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import HeaderActions from "../components/HeaderActions";
import Sidebar from "../components/Sidebar";

/* ==========================================
   DESIGN TOKENS (same theme, flat + professional)
   Plum:  #3D2542 (ink)  #5B3A60 (brand)  #4A2E50 (brand-dark)  #F0E6F5 (tint)  #FAF8FB (page bg)
   Gold:  #B89D4F (accent)  #A88D3F (accent-dark)
   Line:  #E4DCE6
   Text:  #7A6B7E (muted)
   State: success #1F9D6C / #E8F7F0   error #C62828 / #FDEEEE
   ========================================== */

const formatDate = (dateStr) => {
  if (!dateStr) return { date: "—", time: "—" };
  const utcDate = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z");
  return {
    date: utcDate.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
    time: utcDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  };
};

const formatCurrency = (amount, currency = "INR") => {
  if (!amount) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", minimumFractionDigits: 2 }).format(amount);
};

const getFriendlyPlanName = (planName) => {
  if (!planName) return "N/A";
  const lower = planName.toLowerCase();
  if (lower.includes("trial")) return "Trial";
  if (lower.includes("starter") || lower.includes("basic")) return "Starter Plan";
  if (lower.includes("pro") || lower.includes("professional")) return "Pro Plan";
  if (lower.includes("enterprise") || lower.includes("premium")) return "Pro Plan";
  return planName;
};

const getBillingCycle = (interval) => {
  if (!interval) return "Monthly";
  const lower = interval.toLowerCase();
  if (lower.includes("year") || lower.includes("annual")) return "Annual";
  return "Monthly";
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.18 }}
      className={`fixed top-5 right-5 z-50 w-80 px-4 py-3.5 rounded-lg flex items-start gap-3 border shadow-sm bg-white ${
        type === "success" ? "border-l-4 border-l-[#1F9D6C]" : "border-l-4 border-l-[#C62828]"
      } border-t border-r border-b border-[#E4DCE6]`}
      role="alert"
      aria-live="polite"
    >
      {type === "success" ? (
        <FiCheckCircle size={18} className="shrink-0 mt-0.5 text-[#1F9D6C]" />
      ) : (
        <FiAlertCircle size={18} className="shrink-0 mt-0.5 text-[#C62828]" />
      )}
      <span className="text-sm font-medium text-[#3D2542] flex-1">{message}</span>
      <button
        onClick={onClose}
        className="p-1 rounded hover:bg-[#F0E6F5] transition-colors text-[#7A6B7E]"
        aria-label="Close notification"
      >
        <FiX size={14} />
      </button>
    </motion.div>
  );
};

const SubscriptionStatusDot = ({ status }) => {
  const isActive = status === "active";
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${isActive ? "bg-[#1F9D6C]" : "bg-[#C62828]"}`} />
      <span className={`text-sm font-semibold ${isActive ? "text-[#1F9D6C]" : "text-[#C62828]"}`}>
        {isActive ? "Active Subscription" : "Subscription Cancelled"}
      </span>
    </div>
  );
};

export default function BillingHistory() {
  const { tenantId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [toast, setToast] = useState(null);

  const companyName = localStorage.getItem("company_name") || "Workspace";

  const [data, setData] = useState({
    history: [],
    current_period: null,
    current_plan: null,
    subscription_status: "active",
    payment_method: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchBillingData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${getApiBase()}/subscriptions/history?tenant_id=${tenantId}`);
      if (!res.ok) throw new Error("Network response was not ok");
      const resData = await res.json();
      setData({
        history: Array.isArray(resData?.history) ? resData.history : [],
        current_period: resData?.current_period || null,
        current_plan: resData?.current_plan || null,
        subscription_status: resData?.subscription_status || "active",
        payment_method: resData?.payment_method || null,
      });
    } catch (err) {
      console.error("Failed to fetch billing history:", err);
      setError(true);
      setData({ history: [], current_period: null, current_plan: null });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const handleCancelSubscription = async () => {
    if (!tenantId) return;
    try {
      setIsCancelling(true);
      const res = await fetch(`${getApiBase()}/subscriptions/cancel?tenant_id=${tenantId}&cancel_immediately=true`, { method: "POST" });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to cancel subscription");
      }
      await fetchBillingData();
      window.dispatchEvent(new Event("subscriptionUpdated"));
      setToast({ type: "success", message: "Subscription cancelled." });
    } catch (err) {
      console.error("Cancel failed:", err);
      setToast({ type: "error", message: err.message || "Something went wrong" });
    } finally {
      setIsCancelling(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const handleRetry = () => fetchBillingData();

  const isTrial = data.current_plan?.name?.toLowerCase().includes("trial");

  const TopBar = ({ label }) => (
    <header className="sticky top-0 z-30 bg-white border-b border-[#E4DCE6] px-8 h-16 flex items-center">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-md hover:bg-[#F0E6F5] transition-colors">
            {sidebarOpen ? <FiChevronLeft className="text-[#7A6B7E]" size={18} /> : <FiChevronRight className="text-[#7A6B7E]" size={18} />}
          </button>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="font-medium text-[#7A6B7E]">{companyName || "Workspace"}</span>
            <span className="text-[#C9BDCC]">/</span>
            <span className="font-semibold text-[#3D2542]">{label}</span>
          </div>
        </div>
        <HeaderActions tenantId={tenantId} fullName={localStorage.getItem("fullname")} />
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins']">
        <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
 />
        <main className="flex-1 min-h-screen transition-all duration-200" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
          <TopBar label="Billing" />
          <div className="flex-1 flex items-center justify-center" style={{ minHeight: "70vh" }}>
            <div className="flex flex-col items-center gap-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
                className="w-10 h-10 rounded-full border-[3px] border-[#B89D4F] border-t-transparent"
              />
              <p className="text-[#7A6B7E] font-medium text-sm">Loading billing history…</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins']">
        <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
 />
        <main className="flex-1 min-h-screen transition-all duration-200" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
          <TopBar label="Billing" />
          <div className="flex items-center justify-center p-6" style={{ minHeight: "70vh" }}>
            <div className="bg-white rounded-lg border border-[#F0C7C7] p-8 text-center max-w-md">
              <div className="w-14 h-14 bg-[#FDEEEE] rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTimesCircle className="text-[#C62828]" size={20} />
              </div>
              <h2 className="text-lg font-bold text-[#3D2542] mb-2">Unable to Load History</h2>
              <p className="text-[#7A6B7E] text-sm mb-6">There was an error loading your billing history.</p>
              <button onClick={handleRetry} className="px-6 py-2.5 bg-[#5B3A60] text-white rounded-md font-semibold text-sm hover:bg-[#4A2E50] transition-colors">
                Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins']">
      <AnimatePresence>{toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}</AnimatePresence>
      <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
/>
      <main className="flex-1 min-h-screen transition-all duration-200" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
        <TopBar label="Billing" />

        <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
          <div className="mb-1">
            <h1 className="text-xl font-bold text-[#3D2542]">Billing history</h1>
            <p className="text-sm text-[#7A6B7E] mt-1">Review your subscription, payments, and receipts.</p>
          </div>

          {data.current_plan && (
            <div className="bg-white rounded-lg border border-[#E4DCE6] p-6">
              {!isTrial && <div className="mb-4">{<SubscriptionStatusDot status={data.subscription_status} />}</div>}
              {isTrial ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#F0E6F5] flex items-center justify-center">
                    <FaCreditCard className="text-[#5B3A60]" size={15} />
                  </div>
                  <div>
                    <div className="text-xs text-[#7A6B7E]">Current Plan</div>
                    <div className="font-semibold text-[#3D2542]">{getFriendlyPlanName(data.current_plan?.name)}</div>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-[#7A6B7E] text-sm">
                    Next billing:{" "}
                    <span className="font-medium text-[#3D2542]">
                      {new Date(data.current_period?.period_end).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </p>
                  <div className="mt-4 pt-4 border-t border-[#E4DCE6] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md bg-[#F0E6F5] flex items-center justify-center">
                        <FaCreditCard className="text-[#5B3A60]" size={15} />
                      </div>
                      <div>
                        <div className="text-xs text-[#7A6B7E]">Current Plan</div>
                        <div className="font-semibold text-[#3D2542]">{getFriendlyPlanName(data.current_plan?.name)}</div>
                        <div className="text-xs text-[#B9A6BC]">{getBillingCycle(data.current_plan?.interval)}</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="text-center px-4 py-2.5 bg-[#FAF8FB] rounded-md border border-[#E4DCE6]">
                        <div className="text-xs text-[#7A6B7E]">Start</div>
                        <div className="font-semibold text-sm text-[#3D2542]">
                          {new Date(data.current_period?.period_start).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      </div>
                      <div className="text-center px-4 py-2.5 bg-[#FAF8FB] rounded-md border border-[#E4DCE6]">
                        <div className="text-xs text-[#7A6B7E]">End</div>
                        <div className="font-semibold text-sm text-[#3D2542]">
                          {new Date(data.current_period?.period_end).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="bg-white rounded-lg border border-[#E4DCE6] overflow-hidden">
            <div className="p-6 border-b border-[#E4DCE6] bg-[#FAF8FB]">
              <h2 className="text-[15px] font-bold text-[#3D2542]">Transaction History</h2>
              <p className="text-[#7A6B7E] text-sm mt-0.5">All your subscription activities and payments</p>
            </div>
            {data.history.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-14 h-14 bg-[#FAF8FB] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaFileInvoice className="text-[#B9A6BC]" size={20} />
                </div>
                <h3 className="text-[15px] font-bold text-[#3D2542] mb-1">No transactions found</h3>
                <p className="text-[#7A6B7E] text-sm max-w-md mx-auto">Your billing history will appear here once you have transactions.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-full">
                  <thead className="bg-[#FAF8FB]">
                    <tr>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Cost</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E4DCE6]">
                    {data.history.map((event, index) => {
                      const { date } = formatDate(event.created_at);
                      const isPaid = event.event_type === "subscription_activated" || event.event_type === "subscription_renewed";
                      const isFailed = event.event_type === "payment_failed";
                      return (
                        <tr key={index} className="hover:bg-[#FAF8FB] transition-colors">
                          <td className="px-6 py-3.5 font-medium text-[#3D2542]">{date}</td>
                          <td className="px-6 py-3.5">
                            <span className="px-2.5 py-0.5 bg-[#F0E6F5] text-[#5B3A60] rounded text-xs font-semibold">{event.plan || "N/A"}</span>
                          </td>
                          <td className="px-6 py-3.5 font-semibold text-[#3D2542]">{event.amount_paise ? formatCurrency(event.amount_paise / 100, event.currency) : "—"}</td>
                          <td className="px-6 py-3.5">
                            {isPaid && <span className="px-2.5 py-0.5 bg-[#E8F7F0] text-[#1F9D6C] border border-[#BFE8D5] rounded text-xs font-semibold">Paid</span>}
                            {isFailed && <span className="px-2.5 py-0.5 bg-[#FDEEEE] text-[#C62828] border border-[#F0C7C7] rounded text-xs font-semibold">Failed</span>}
                            {!isPaid && !isFailed && <span className="px-2.5 py-0.5 bg-[#FAF8FB] text-[#7A6B7E] border border-[#E4DCE6] rounded text-xs font-semibold">—</span>}
                          </td>
                          <td className="px-6 py-3.5">
                            {event.razorpay_invoice_id ? (
                              <a
                                href={`${getApiBase()}/subscriptions/invoice/${event.razorpay_invoice_id}?tenant_id=${tenantId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#FAF8FB] border border-[#E4DCE6] hover:border-[#5B3A60] text-[#5B3A60] transition-colors"
                                title="View Receipt / Invoice"
                              >
                                <FaFileInvoice size={13} />
                              </a>
                            ) : (
                              <span className="text-[#C9BDCC]">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {data.history.length > 0 && (
              <div className="p-4 border-t border-[#E4DCE6] bg-[#FAF8FB] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm text-[#7A6B7E]">
                  Showing <span className="font-medium text-[#3D2542]">{data.history.length}</span> transactions
                </p>
                <button
                  className="px-4 py-2 bg-white border border-[#E4DCE6] rounded-md text-[#5B3A60] font-medium text-sm hover:border-[#5B3A60] transition-colors flex items-center gap-2"
                  onClick={() => setToast({ type: "success", message: "Export feature coming soon!" })}
                >
                  <FaDownload size={12} />
                  Export All
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-[#E4DCE6] p-6">
            <h3 className="text-[15px] font-bold text-[#3D2542] mb-4">Payment Method</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-7 bg-[#3D2542] rounded flex items-center justify-center">
                  <FaCreditCard className="text-white" size={13} />
                </div>
                <div>
                  <div className="font-medium text-[#3D2542]">
                    {data.payment_method?.method === "card" && (
                      <>
                        {data.payment_method.card_network?.toUpperCase()} •••• {data.payment_method.card_last4}
                      </>
                    )}
                    {data.payment_method?.method === "upi" && <>UPI • {data.payment_method.upi_id}</>}
                    {!data.payment_method && "No payment method found"}
                  </div>
                  <div className="text-xs text-[#7A6B7E]">{data.payment_method?.method === "card" && "Saved Card"}</div>
                </div>
              </div>
            </div>
          </div>

          {!isTrial && data.subscription_status !== "cancelled" && (
            <div className="flex justify-end">
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="px-5 py-2.5 text-sm font-semibold text-[#C62828] border border-[#F0C7C7] rounded-md bg-[#FDEEEE] hover:border-[#C62828] transition-colors disabled:opacity-60"
              >
                {isCancelling ? "Cancelling…" : "Cancel Subscription"}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}